/**
 * NavbarExamples.tsx
 * Comprehensive examples of HeroUI Navbar component suite
 * 
 * Showcases all HeroUI Navbar features with brand icons, navigation items, 
 * mobile responsiveness, and theme integration
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Brand } from '@enfomentis/shared/config';
import { 
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenuToggle,
  NavbarMenu,
  NavbarMenuItem
} from '@enfomentis/ui';
import { Link } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { ButtonGroup } from '@enfomentis/ui';
import { Divider } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { TrendingUp, User } from 'lucide-react';
import { BrandMark, BrandText } from '@enfomentis/ui';


export const NavbarExamples: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('light');

  const themeOptions = [
    { label: 'Light', value: 'light' },
    { label: 'Dark', value: 'dark' },
    { label: 'Auto', value: 'auto' }
  ];

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Navbar */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Navigation Bar</h3>
          <Card>
            <CardBody className="p-0">
              <Navbar isBordered>
                <NavbarBrand>
                  <BrandMark size={28} className="mr-2 -mt-1" />
                  <BrandText height={16} className="text-text-primary" />
                </NavbarBrand>
                <NavbarContent justify="center">
                  <NavbarItem>
                    <Link href="#dashboard" color="primary">Dashboard</Link>
                  </NavbarItem>
                  <NavbarItem>
                    <Link href="#trades" color="foreground">Trades</Link>
                  </NavbarItem>
                  <NavbarItem>
                    <Link href="#analytics" color="foreground">Analytics</Link>
                  </NavbarItem>
                </NavbarContent>
                <NavbarContent justify="end">
                  <NavbarItem>
                    <Button color="primary" variant="flat" size="sm">
                      Sign In
                    </Button>
                  </NavbarItem>
                </NavbarContent>
              </Navbar>
            </CardBody>
          </Card>
        </div>

        {/* Brand Icon Variations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Alternative Brand Icons</h3>
          <div className="space-y-4">
            
            {/* Alternative with Different Text */}
            <Card>
              <CardHeader>
                <h4 className="text-sm font-medium text-text-secondary">Compact Brand</h4>
              </CardHeader>
              <CardBody className="p-0">
                <Navbar>
                  <NavbarBrand>
                    <BrandMark size={24} className="mr-2 -mt-1" />
                    <BrandText height={16} className="text-text-primary" />
                  </NavbarBrand>
                  <NavbarContent justify="end">
                    <NavbarItem>
                      <Link href="#profile" color="foreground">Profile</Link>
                    </NavbarItem>
                  </NavbarContent>
                </Navbar>
              </CardBody>
            </Card>

            {/* Alternative with Full Name */}
            <Card>
              <CardHeader>
                <h4 className="text-sm font-medium text-text-secondary">Full Brand Name</h4>
              </CardHeader>
              <CardBody className="p-0">
                <Navbar>
                  <NavbarBrand>
                    <BrandMark size={24} className="mr-2 -mt-1" />
                    <BrandText height={16} className="text-text-primary" />
                  </NavbarBrand>
                  <NavbarContent justify="end">
                    <NavbarItem>
                      <Link href="#settings" color="foreground">Settings</Link>
                    </NavbarItem>
                  </NavbarContent>
                </Navbar>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Mobile Responsive Navbar */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Mobile Responsive</h3>
          <Card>
            <CardHeader>
              <h4 className="text-sm font-medium text-text-secondary">With Mobile Menu</h4>
            </CardHeader>
            <CardBody className="p-0">
              <Navbar 
                onMenuOpenChange={setIsMenuOpen}
                isMenuOpen={isMenuOpen}
                isBordered
              >
                <NavbarContent>
                  <NavbarMenuToggle
                    aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                    className="sm:hidden"
                  />
                  <NavbarBrand>
                    <BrandMark size={26} className="mr-2 -mt-1" />
                    <BrandText height={16} className="text-text-primary" />
                  </NavbarBrand>
                </NavbarContent>

                <NavbarContent className="hidden sm:flex gap-4" justify="center">
                  <NavbarItem>
                    <Link href="#dashboard" color="primary">Dashboard</Link>
                  </NavbarItem>
                  <NavbarItem>
                    <Link href="#trades" color="foreground">Trades</Link>
                  </NavbarItem>
                  <NavbarItem>
                    <Link href="#analytics" color="foreground">Analytics</Link>
                  </NavbarItem>
                  <NavbarItem>
                    <Link href="#reports" color="foreground">Reports</Link>
                  </NavbarItem>
                </NavbarContent>

                <NavbarContent justify="end">
                  <NavbarItem className="hidden sm:flex">
                    <Button color="primary" variant="flat" size="sm">
                      Sign In
                    </Button>
                  </NavbarItem>
                </NavbarContent>

                <NavbarMenu>
                  <NavbarMenuItem>
                    <Link href="#dashboard" color="primary" size="lg">
                      Dashboard
                    </Link>
                  </NavbarMenuItem>
                  <NavbarMenuItem>
                    <Link href="#trades" color="foreground" size="lg">
                      Trades
                    </Link>
                  </NavbarMenuItem>
                  <NavbarMenuItem>
                    <Link href="#analytics" color="foreground" size="lg">
                      Analytics
                    </Link>
                  </NavbarMenuItem>
                  <NavbarMenuItem>
                    <Link href="#reports" color="foreground" size="lg">
                      Reports
                    </Link>
                  </NavbarMenuItem>
                  <NavbarMenuItem>
                    <Link href="#signin" color="primary" size="lg">
                      Sign In
                    </Link>
                  </NavbarMenuItem>
                </NavbarMenu>
              </Navbar>
            </CardBody>
          </Card>
        </div>

        {/* Navbar with Actions */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">With Action Buttons</h3>
          <Card>
            <CardBody className="p-0">
              <Navbar isBordered>
                <NavbarBrand>
                  <BrandMark size={24} className="mr-2 -mt-1" />
                  <span className="font-bold text-text-primary">Trading Platform</span>
                </NavbarBrand>
                <NavbarContent justify="center">
                  <NavbarItem>
                    <Link href="#portfolio" color="foreground">Portfolio</Link>
                  </NavbarItem>
                  <NavbarItem>
                    <Link href="#watchlist" color="foreground">Watchlist</Link>
                  </NavbarItem>
                  <NavbarItem>
                    <Link href="#orders" color="foreground">Orders</Link>
                  </NavbarItem>
                </NavbarContent>
                <NavbarContent justify="end">
                  <NavbarItem>
                    <Button
                      color="success"
                      variant="flat"
                      size="sm"
                      startContent={<TrendingUp className="w-4 h-4" />}
                    >
                      New Trade
                    </Button>
                  </NavbarItem>
                  <NavbarItem>
                    <Button
                      color="default"
                      variant="flat"
                      size="sm"
                      isIconOnly
                    >
                      <User className="w-4 h-4" />
                    </Button>
                  </NavbarItem>
                </NavbarContent>
              </Navbar>
            </CardBody>
          </Card>
        </div>

        {/* Theme Integration Demo */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Theme Integration</h3>
          <Card>
            <CardHeader>
              <h4 className="text-sm font-medium text-text-secondary">Navbar with Theme Switcher</h4>
            </CardHeader>
            <CardBody className="p-0">
              <Navbar isBordered>
                <NavbarBrand>
                  <BrandMark size={28} className="mr-2 -mt-1" />
                  <span className="font-bold text-text-primary">{Brand.shortName} Dashboard</span>
                </NavbarBrand>
                <NavbarContent justify="center">
                  <NavbarItem>
                    <Link href="#home" color="primary">Home</Link>
                  </NavbarItem>
                  <NavbarItem>
                    <Link href="#about" color="foreground">About</Link>
                  </NavbarItem>
                  <NavbarItem>
                    <Link href="#contact" color="foreground">Contact</Link>
                  </NavbarItem>
                </NavbarContent>
                <NavbarContent justify="end">
                  <NavbarItem>
                    <ButtonGroup
                      options={themeOptions}
                      value={selectedTheme}
                      onChange={(value) => setSelectedTheme(String(value))}
                      variant="flat"
                      size="sm"
                    />
                  </NavbarItem>
                </NavbarContent>
              </Navbar>
            </CardBody>
          </Card>
        </div>

        {/* Sticky Navbar */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Sticky Positioning</h3>
          <Card>
            <CardHeader>
              <h4 className="text-sm font-medium text-text-secondary">Sticky Navbar (position="sticky")</h4>
            </CardHeader>
            <CardBody className="p-0">
              <Navbar position="sticky" isBordered>
                <NavbarBrand>
                  <BrandMark size={24} className="mr-2 -mt-1" />
                  <span className="font-bold text-text-primary">Sticky Nav</span>
                </NavbarBrand>
                <NavbarContent justify="center">
                  <NavbarItem>
                    <Link href="#section1" color="foreground">Section 1</Link>
                  </NavbarItem>
                  <NavbarItem>
                    <Link href="#section2" color="foreground">Section 2</Link>
                  </NavbarItem>
                  <NavbarItem>
                    <Link href="#section3" color="foreground">Section 3</Link>
                  </NavbarItem>
                </NavbarContent>
              </Navbar>
              {/* Sample content to demonstrate sticky behavior */}
              <div className="p-4 space-y-4">
                <p className="text-text-secondary text-sm">
                  This navbar will stick to the top when scrolling (in real usage).
                </p>
                <Divider />
                <p className="text-text-secondary text-sm">
                  Content below the navbar...
                </p>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Usage Code Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Usage Examples</h3>
          <Card>
            <CardHeader>
              <h4 className="text-sm font-medium text-text-secondary">Import & Basic Usage</h4>
            </CardHeader>
            <CardBody>
              <pre className="text-sm text-text-secondary bg-surface p-4 rounded-lg overflow-x-auto">
{`import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenuToggle,
  NavbarMenu,
  NavbarMenuItem
} from '@enfomentis/ui';

<Navbar isBordered>
  <NavbarBrand>
    <TrendingUp className="w-6 h-6 text-primary mr-2" />
    <span className="font-bold">{Brand.shortName}</span>
  </NavbarBrand>
  <NavbarContent justify="center">
    <NavbarItem>
      <Link href="/dashboard" color="primary">
        Dashboard
      </Link>
    </NavbarItem>
  </NavbarContent>
</Navbar>`}
              </pre>
            </CardBody>
          </Card>
        </div>

      </div>
    </PlaygroundPageLayout>
  );
};